import { useState } from 'react';
import { StatsSheet } from './components/StatsSheet';
import { ProfileHeader } from './components/ProfileHeader';
import { PhotoGrid } from './components/PhotoGrid';
import { ParentProfile } from './components/ParentProfile';
import { CoachProfile } from './components/CoachProfile';
import { useWrestlerStats } from './hooks/useWrestlerStats';
import { useAuthStore } from '@/store/authStore';
import { Grid, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';

export function ProfilePage() {
    const account = useAuthStore(s => s.account);
    const stats = useWrestlerStats();
    const [isStatsOpen, setIsStatsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<'posts' | 'accolades'>('posts');

    if (account?.role === 'parent') {
        return <ParentProfile />;
    }

    if (account?.role === 'coach') {
        return <CoachProfile />;
    }

    return (
        <div className="min-h-screen bg-bg pb-32 animate-fade-in text-text-primary">
            <div className="max-w-4xl mx-auto">
                {/* 1. Header Section */}
                <ProfileHeader
                    onOpenStats={() => setIsStatsOpen(true)}
                    stats={stats}
                />

                {/* 2. Tabs */}
                <div className="px-4 md:px-8 mt-4">
                    <div className="flex gap-4 border-b border-white/10 pb-[-1px]">
                        <button
                            onClick={() => setActiveTab('posts')}
                            className={cn(
                                "flex items-center gap-2 py-4 px-2 text-sm font-bold uppercase tracking-widest transition-all relative",
                                activeTab === 'posts'
                                    ? "text-white"
                                    : "text-text-muted hover:text-text-secondary"
                            )}
                        >
                            <Grid size={16} />
                            <span>Posts</span>
                            {activeTab === 'posts' && (
                                <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gold-500 rounded-t-full shadow-[0_0_10px_rgba(212,175,55,0.5)]" />
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('accolades')}
                            className={cn(
                                "flex items-center gap-2 py-4 px-2 text-sm font-bold uppercase tracking-widest transition-all relative",
                                activeTab === 'accolades'
                                    ? "text-white"
                                    : "text-text-muted hover:text-text-secondary"
                            )}
                        >
                            <Trophy size={16} />
                            <span>Accolades</span>
                            {activeTab === 'accolades' && (
                                <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gold-500 rounded-t-full shadow-[0_0_10px_rgba(212,175,55,0.5)]" />
                            )}
                        </button>
                    </div>
                </div>

                {/* 3. Content Grid */}
                <div key={activeTab} className="mt-6 px-4 md:px-8 pb-20 animate-fade-in-up">
                    {activeTab === 'posts' ? (
                        <PhotoGrid />
                    ) : (
                        <div className="py-20 text-center text-text-muted">
                            <Trophy className="w-12 h-12 mx-auto mb-4 opacity-20" />
                            <p>No accolades pinned yet.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Hidden Stats Sheet Modal */}
            <StatsSheet
                isOpen={isStatsOpen}
                onClose={() => setIsStatsOpen(false)}
                stats={stats}
            />
        </div>
    );
}

